import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {Helmet} from 'react-helmet-async'
import {Col, Container, Row} from 'react-bootstrap'
import DefaultLayout from '../../layouts/Default'
import {isSanityConfigured} from '../../lib/sanityClient'
import {getAllPosts} from '../../lib/sanityQueries'
import {urlForImage} from '../../lib/sanityImage'
import authorImage from '../../assets/images/bio-headshot.png'

const BLOG_AUTHOR_NAME = 'Drew'
const WORDS_PER_MINUTE = 220

function formatPostDate(iso) {
  if (!iso) return null
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(iso))
  } catch {
    return null
  }
}

function formatRelativeTime(iso) {
  if (!iso) return null
  const publishedMs = new Date(iso).getTime()
  if (Number.isNaN(publishedMs)) return null

  const elapsedMs = Date.now() - publishedMs
  const hourMs = 60 * 60 * 1000
  const dayMs = 24 * hourMs
  const monthMs = 30 * dayMs

  if (elapsedMs < hourMs) return 'just now'
  if (elapsedMs < dayMs) return `${Math.floor(elapsedMs / hourMs)}h ago`
  if (elapsedMs < monthMs) return `${Math.floor(elapsedMs / dayMs)}d ago`
  return `${Math.floor(elapsedMs / monthMs)}mo ago`
}

function estimateReadTime(body) {
  if (!Array.isArray(body) || !body.length) return '1 min read'

  const text = body
    .map((node) => {
      if (node?._type !== 'block' || !Array.isArray(node.children)) return ''
      return node.children.map((child) => child?.text || '').join(' ')
    })
    .join(' ')
    .trim()

  const words = text ? text.split(/\s+/).filter(Boolean).length : 0
  const minutes = Math.max(1, Math.ceil(words / WORDS_PER_MINUTE))
  return `${minutes} min read`
}

const Blog = () => {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isSanityConfigured()) {
      setLoading(false)
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    getAllPosts()
      .then((data) => {
        if (!cancelled) setPosts(Array.isArray(data) ? data : [])
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const renderBody = () => {
    if (!isSanityConfigured()) {
      return (
        <div className="blog-setup-hint" role="status">
          <p>
            Blog content is powered by Sanity. Add <code>REACT_APP_SANITY_PROJECT_ID</code> and{' '}
            <code>REACT_APP_SANITY_DATASET</code> to <code>.env.local</code>, then restart the dev server.
          </p>
          <p>
            See <code>docs/blog-authoring.md</code> in the repository for the full setup.
          </p>
        </div>
      )
    }

    if (loading) {
      return <p className="blog-muted">Loading posts…</p>
    }

    if (error) {
      return (
        <div className="blog-error" role="alert">
          <p>Could not load posts.</p>
          <p className="blog-error__detail">{error.message || String(error)}</p>
          <p className="blog-muted">
            If this is a CORS issue, add your site origin under API → CORS origins in the Sanity project.
          </p>
        </div>
      )
    }

    if (!posts.length) {
      return <p className="blog-muted">No posts yet. Publish a post in Sanity Studio to see it here.</p>
    }

    return (
      <ul className="blog-list list-unstyled">
        {posts.map((post) => {
          const thumb = urlForImage(post.featuredImage)?.width(900).height(560).fit('crop').auto('format').url()
          const relativeDate = formatRelativeTime(post.publishedAt)
          const readTime = estimateReadTime(post.body)

          return (
            <li key={post._id} className="blog-card">
              <Link to={`/blog/${post.slug}`} className="blog-card__link">
                {thumb ? (
                  <div className="blog-card__media">
                    <img src={thumb} alt="" className="blog-card__img" loading="lazy" />
                  </div>
                ) : (
                  <div className="blog-card__media blog-card__media--placeholder" aria-hidden />
                )}
                <div className="blog-card__body">
                  <h2 className="blog-card__title">{post.title}</h2>
                  {post.excerpt ? <p className="blog-card__excerpt">{post.excerpt}</p> : null}
                  <div className="blog-card__meta">
                    <div className="blog-card__author">
                      <img src={authorImage} alt={BLOG_AUTHOR_NAME} className="blog-card__author-img" loading="lazy" />
                      <span className="blog-card__author-name">{BLOG_AUTHOR_NAME}</span>
                    </div>
                    <span className="blog-card__dot" aria-hidden>
                      •
                    </span>
                    <div className="blog-card__meta-right">
                      {relativeDate ? <span className="blog-card__relative-time">{relativeDate}</span> : null}
                      <span className="blog-card__dot" aria-hidden>
                        •
                      </span>
                      <span className="blog-card__read-time">
                        <span className="blog-card__ai-icon" aria-hidden>
                          ✦
                        </span>
                        {readTime}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <DefaultLayout pageTitle="blog" wideContent>
      <Helmet>
        <title>Blog — itsmedrew</title>
        <meta name="description" content="Writing on design, product, and the web." />
      </Helmet>
      <Container>
        <Row>
          <Col>
            <header className="blog-page-header">
              <h1 className="blog-page-title">Blog</h1>
            </header>
            {renderBody()}
          </Col>
        </Row>
      </Container>
    </DefaultLayout>
  )
}

export default Blog
